import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toPng } from 'html-to-image';
import { toast } from 'react-hot-toast';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import ErrorDialog from '../components/ui/ErrorDialog';
import ListPagination, { LIST_PAGE_SIZE } from '../components/ui/ListPagination';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import OfferSearchTypeahead from '../components/OfferSearchTypeahead';
import { api } from '../services/authService';
import { OFFER_REPORT_LIST_PAGE_KEY, listPageState, useListPage } from '../utils/listPage';
import { sanitizeVendorText } from '../utils/signalProviders';
import { organizationsService } from '../services/organizationsService';
import usePermissions from '../hooks/usePermissions';
import { PERMISSIONS } from '../services/permissions';
import { HEADER_SELECT_CLASS, PageHeaderActions } from '../components/PageHeaderActions';
import {
  buildCriteriaFromAttributeRows,
  buildCriteriaFromVertical,
  GENERAL_TEMPLATE_VALUE,
} from '../components/segmentBuilder/verticalTemplate';
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  DollarSign,
  Download,
  HelpCircle,
  MousePointerClick,
  Users,
} from 'lucide-react';

const ATTRIBUTES_LIMIT = 10;
const TOP_ATTRIBUTES_TOOLTIP =
  'Only attribute values showing a strong positive predictive potential will be displayed';
const BOTTOM_ATTRIBUTES_TOOLTIP =
  'Only attribute values showing a strong negative predictive potential will be displayed';
const TOOLTIP_WIDTH_PX = 288;

/** Report sheet palette, from offer_report_v2.html. */
const REPORT_COLORS = {
  paper: '#f4f7fb',
  include: '#00B7D0',
  exclude: '#FF87AA',
  amber: '#8A46F2',
  blue: '#0062FB',
  clickers: '#53A5FF',
  baseline: '#C7CBD1',
};

const SORTABLE_COLUMNS = [
  { key: 'offer_id', label: 'Offer ID' },
  { key: 'offer_name', label: 'Offer Name' },
  { key: 'advertiser_name', label: 'Advertiser' },
  { key: 'category', label: 'Category' },
  { key: 'total_click', label: 'Total Clicks', align: 'right' },
  { key: 'total_revenue', label: 'Total Revenue', align: 'right' },
  { key: 'created', label: 'Created' },
];

function SectionTitleWithTooltip({ children, tooltip }) {
  const [visible, setVisible] = React.useState(false);
  const [alignRight, setAlignRight] = React.useState(false);
  const ref = React.useRef(null);

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const wouldOverflowRight = rect.left + TOOLTIP_WIDTH_PX > window.innerWidth;
      setAlignRight(wouldOverflowRight);
    }
    setVisible(true);
  };

  const handleMouseLeave = () => setVisible(false);

  return (
    <span
      ref={ref}
      className="relative inline-flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <span
        className="ml-1 cursor-help text-gray-400 hover:text-gray-600 flex-shrink-0"
        aria-label={tooltip}
      >
        <HelpCircle className="w-4 h-4" />
      </span>
      <span
        role="tooltip"
        className={`absolute bottom-full mb-1 z-50 w-72 p-2.5 text-xs font-normal text-left text-white bg-gray-900 rounded shadow-lg whitespace-pre-line ${
          visible ? 'block' : 'hidden'
        } ${alignRight ? 'right-0' : 'left-0'}`}
      >
        {tooltip}
      </span>
    </span>
  );
}

function ReportSection({ title, actions, children }) {
  return (
    <section className="mb-4 rounded-[24px] border border-[#E2E8F0] bg-white px-6 py-5 shadow-[0_1px_3px_rgba(22,27,34,0.06)]">
      <div className="mb-3.5 flex items-center justify-between gap-4">
        <h2 className="m-0 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#8A8F98]">
          {title}
        </h2>
        {actions || null}
      </div>
      {children}
    </section>
  );
}

const REC_TONES = {
  include: { accent: REPORT_COLORS.include, text: 'text-[#00B7D0]' },
  exclude: { accent: REPORT_COLORS.exclude, text: 'text-[#FF87AA]' },
  warning: { accent: REPORT_COLORS.amber, text: 'text-[#8A46F2]' },
};

function RecommendationCard({ tone, title, children }) {
  const { accent, text } = REC_TONES[tone];
  return (
    <div
      className="rounded-[18px] border border-[#EEF2F7] border-l-4 bg-[#f4f7fb] px-[18px] py-4 shadow-[0_1px_2px_rgba(22,27,34,0.05)]"
      style={{ borderLeftColor: accent }}
    >
      <h3 className={`mb-2 text-[15px] font-semibold ${text}`}>{title}</h3>
      {children}
    </div>
  );
}

function PairList({ rows }) {
  return (
    <ul className="m-0 list-none p-0">
      {rows.map((row) => (
        <li
          key={row.key}
          className="flex justify-between gap-3 border-t border-[#EEF2F7] py-1.5 text-sm first:border-t-0"
        >
          <span className="text-[#2B323C]">{row.attribute}</span>
          <span className="text-right text-[13px] text-[#8A8F98]">{row.value}</span>
        </li>
      ))}
    </ul>
  );
}

function NotesCard({ children }) {
  return (
    <div className="rounded-[18px] border border-[#EEF2F7] bg-[#f4f7fb] px-[18px] py-4 text-sm text-[#2B323C] shadow-[0_1px_2px_rgba(22,27,34,0.05)]">
      <h3 className="mb-1.5 text-[13px] font-semibold text-[#8A8F98]">Notes</h3>
      {children}
    </div>
  );
}

const KPI_ICON_TONES = {
  blue: { wrap: 'bg-[#E3ECFF]', icon: 'text-[#0062FB]' },
  green: { wrap: 'bg-[#DFF7FA]', icon: 'text-[#00B7D0]' },
};

function KpiCell({ label, value, icon, iconTone = 'blue' }) {
  const tone = KPI_ICON_TONES[iconTone];
  return (
    <div className="rounded-[24px] border border-[#E2E8F0] bg-white px-5 py-[18px] shadow-[0_1px_3px_rgba(22,27,34,0.06)]">
      <div className="mb-3 flex items-center gap-2">
        <div
          className={`flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px] ${tone.wrap} ${tone.icon}`}
        >
          {icon}
        </div>
        <div className="text-[11.5px] font-semibold uppercase tracking-[0.05em] text-[#8A8F98]">
          {label}
        </div>
      </div>
      <div className="text-[28px] font-bold tracking-[-0.01em] tabular-nums text-[#161B22]">
        {value}
      </div>
    </div>
  );
}

function HeaderPill({ label, value }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white px-[18px] py-3 text-sm text-[#8A8F98] shadow-[0_1px_2px_rgba(22,27,34,0.05)]">
      {label}{' '}
      <strong className="font-semibold tabular-nums text-[#161B22]">{value}</strong>
    </span>
  );
}

const ATTRIBUTE_BAR_CAPTION = 'Share of total, by value — vs. even-split baseline';

/** Bar widths are percentages already on a 0–100 scale. */
function barWidth(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function ValueBar({ label, color, value, format }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-[52px] flex-none text-[10.5px] text-[#8A8F98]">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#E8EEF5]">
        <div
          className="h-full rounded-full"
          style={{ width: `${barWidth(value)}%`, backgroundColor: color }}
        />
      </div>
      <span className="w-[38px] flex-none text-right text-[11.5px] tabular-nums text-[#2B323C]">
        {format(value)}
      </span>
    </div>
  );
}

function ReportLegend({ metricView }) {
  const valueColor =
    metricView === 'clicks' ? REPORT_COLORS.clickers : REPORT_COLORS.blue;
  const valueLabel =
    metricView === 'clicks' ? 'Share of clicks' : 'Share of conversions';
  return (
    <div className="flex flex-wrap gap-4 px-1 text-[11.5px] text-[#8A8F98]">
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block h-2 w-2 rounded-[2px]"
          style={{ backgroundColor: valueColor }}
        />
        {valueLabel}
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block h-2 w-2 rounded-[2px]"
          style={{ backgroundColor: REPORT_COLORS.baseline }}
        />
        Even-split baseline
      </span>
    </div>
  );
}

function formatReportDate(value) {
  if (!value) return '—';
  const raw = typeof value === 'string' ? value.slice(0, 10) : value;
  const parsed = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(raw);
  return format(parsed, 'MMM d, yyyy');
}

function formatDate(value) {
  if (!value) return '—';
  const d = typeof value === 'string' ? value.slice(0, 10) : value;
  return String(d);
}

function formatCount(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString();
}

function formatRevenue(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;
}

function formatFeatureLabel(feature) {
  const displayLabel = feature?.display_label?.trim();
  if (displayLabel) return sanitizeVendorText(displayLabel);
  const name = feature?.feature;
  if (!name) return '—';
  const spaced = String(name).replaceAll('_', ' ');
  const titled = spaced.charAt(0).toUpperCase() + spaced.slice(1);
  return sanitizeVendorText(titled);
}

function formatValueLabel(row) {
  const valueLabel = row?.value_label?.trim();
  if (valueLabel) return sanitizeVendorText(valueLabel);
  const value = row?.value;
  if (value == null || value === '') return '—';
  const spaced = String(value).replaceAll('_', ' ');
  const titled = spaced.charAt(0).toUpperCase() + spaced.slice(1);
  return sanitizeVendorText(titled);
}

function formatPercent(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `${n.toFixed(2)}%`;
}

function isMissingValue(value) {
  return value == null || value === '';
}

function allFieldMissing(rows, field) {
  return rows.every((row) => isMissingValue(row?.[field]));
}

/** pct_of_universe is already percent-scaled (e.g. 30.011), not a 0–1 fraction. */
function formatUniverseShare(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `${n.toFixed(2)}%`;
}

function formatPairAttribute(row, featureLabelByName) {
  const featureKey = row?.feature;
  return formatFeatureLabel({
    feature: featureKey,
    display_label: row?.display_label || (featureKey && featureLabelByName?.[featureKey]),
  });
}

function formatPairValue(row) {
  const valueText = formatValueLabel(row);
  const universePct = row?.pct_of_universe;
  if (!isMissingValue(universePct)) {
    return `${valueText} (${formatUniverseShare(universePct)} of the list)`;
  }
  const convertersPct = row?.pct_of_converters;
  if (!isMissingValue(convertersPct)) {
    return `${valueText} (${formatPercent(convertersPct)} of converters)`;
  }
  return valueText;
}

function toPairRows(rows, featureLabelByName) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows.map((row, index) => ({
    key: `${row?.feature ?? 'feature'}:${row?.value ?? index}`,
    attribute: formatPairAttribute(row, featureLabelByName),
    value: formatPairValue(row),
  }));
}

function formatWarningClause(row, featureLabelByName) {
  if (!row?.feature) return null;
  const featureLabel = formatFeatureLabel({
    feature: row.feature,
    display_label: row.display_label || featureLabelByName?.[row.feature],
  });
  const unknownPct = formatPercent(row.pct_of_converters);
  const unknownText = formatValueLabel(row);
  const parts = [`${featureLabel} is ${unknownText} for ${unknownPct}.`];
  if (row.best_value != null && row.best_value !== '') {
    const bestValueText = formatValueLabel({
      value: row.best_value,
      value_label: row.best_value_label,
    });
    if (row.best_pct_of_universe != null && row.best_pct_of_universe !== '') {
      const bestUniversePct = formatUniverseShare(row.best_pct_of_universe);
      parts.push(
        `${featureLabel} ${bestValueText} converts best but reaches only ${bestUniversePct} of the list.`
      );
    } else {
      parts.push(`${featureLabel} ${bestValueText} converts best.`);
    }
  }
  parts.push(`Do not require ${featureLabel.toLowerCase()} by default.`);
  return parts.join(' ');
}

function compareOfferRows(a, b, field, order) {
  const av = a?.[field];
  const bv = b?.[field];
  const aMissing = av == null || av === '';
  const bMissing = bv == null || bv === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  let cmp = 0;
  if (field === 'offer_id' || field === 'total_click' || field === 'total_revenue') {
    cmp = Number(av) - Number(bv);
  } else if (field === 'created') {
    cmp = String(av).localeCompare(String(bv));
  } else {
    cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
  }
  return order === 'asc' ? cmp : -cmp;
}

function getDetailsMode(offer) {
  if (!offer) return null;
  if (offer.has_metrics) return 'metrics';
  if (offer.has_values) return 'values';
  if (offer.has_vertical) return 'vertical';
  return 'general';
}

function isCategoryDetailsMode(mode) {
  return mode === 'vertical' || mode === 'general';
}

/** Short badge label naming which of the four report variants is shown. */
function detailsTypeLabel(offer) {
  const mode = getDetailsMode(offer);
  if (mode === 'values') return 'Lite';
  if (mode === 'vertical') return `Vertical — ${offer?.category || 'Uncategorized'}`;
  if (mode === 'general') return 'General';
  return 'Full Report';
}

function sanitizeSegmentName(value) {
  return String(value || '').replace(/[^a-zA-Z0-9_ -]/g, '');
}

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function exportFileName(offer) {
  const parts = [
    'offer-report',
    offer?.offer_id,
    slugify(detailsTypeLabel(offer)),
    format(new Date(), 'yyyy-MM-dd'),
  ].filter(Boolean);
  return `${parts.join('-')}.png`;
}

function detailsApiPaths(offer) {
  const id = offer.offer_id;
  const mode = getDetailsMode(offer);
  if (mode === 'values') {
    return {
      features: `/api/v2/offer_reports/${id}/values_features`,
      featureValues: `/api/v2/offer_reports/${id}/values_feature_values`,
    };
  }
  if (mode === 'vertical') {
    return {
      features: `/api/v2/offer_reports/${id}/vertical_features`,
      featureValues: `/api/v2/offer_reports/${id}/vertical_feature_values`,
    };
  }
  if (mode === 'general') {
    return {
      features: `/api/v2/offer_reports/${id}/general_features`,
      featureValues: `/api/v2/offer_reports/${id}/general_feature_values`,
    };
  }
  return {
    features: `/api/v2/offer_reports/${id}/features`,
    topPairs: `/api/v2/offer_reports/${id}/top_pairs`,
    featureValues: `/api/v2/offer_reports/${id}/feature_values`,
  };
}

function OfferReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { listOffset, currentPage, goToPage, resetPage } = useListPage({
    storageKey: OFFER_REPORT_LIST_PAGE_KEY,
  });
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accessError, setAccessError] = useState('');
  const [matchIds, setMatchIds] = useState(null);
  const [searching, setSearching] = useState(false);
  const [listSearchError, setListSearchError] = useState('');
  const searchQueryRef = useRef('');
  const [sortByField, setSortByField] = useState('total_revenue');
  const [sortOrder, setSortOrder] = useState('desc');
  const [detailsOffer, setDetailsOffer] = useState(null);
  const [topAttributes, setTopAttributes] = useState([]);
  const [bottomAttributes, setBottomAttributes] = useState([]);
  const [topAttributesLoading, setTopAttributesLoading] = useState(false);
  const [bottomAttributesLoading, setBottomAttributesLoading] = useState(false);
  const [topAttributesError, setTopAttributesError] = useState('');
  const [bottomAttributesError, setBottomAttributesError] = useState('');
  const [topPairs, setTopPairs] = useState({ positive: [], negative: [], warning: null });
  const [topPairsLoading, setTopPairsLoading] = useState(false);
  const [topPairsError, setTopPairsError] = useState('');
  const [expandedAttributeKey, setExpandedAttributeKey] = useState(null);
  const [featureValues, setFeatureValues] = useState({});
  const [featureValuesLoading, setFeatureValuesLoading] = useState(false);
  const [featureValuesError, setFeatureValuesError] = useState('');
  const [exportingPng, setExportingPng] = useState(false);
  const [generatingSegment, setGeneratingSegment] = useState(false);
  const { can } = usePermissions();
  const canExport = can(PERMISSIONS.EXPORT);
  const canCreateSegments = can(PERMISSIONS.SEGMENTS_CREATE);
  /** Captured for the PNG; export and OK controls are dropped from the image. */
  const reportRef = useRef(null);

  useEffect(() => {
    const message = location.state?.offerAccessError;
    if (typeof message !== 'string' || !message) return;
    setAccessError(message);
    const nextState = { ...(location.state || {}) };
    delete nextState.offerAccessError;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: nextState });
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        if (cancelled) return;
        setOrganizations(orgs);

        const savedSelected = organizationsService.getSelectedOrganization('id');
        if (savedSelected && orgs.some((o) => String(o.id) === String(savedSelected))) {
          setSelectedOrganization(String(savedSelected));
          organizationsService.setSelectedOrganization(savedSelected, 'id', orgs);
        } else if (orgs.length > 0) {
          const defaultId = String(orgs[0].id);
          setSelectedOrganization(defaultId);
          organizationsService.setSelectedOrganization(defaultId, 'id', orgs);
        }
      } catch {
        if (!cancelled) {
          setOrganizations([]);
          setError('Failed to load organizations. Please refresh the page and try again.');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadRows = useCallback(async () => {
    if (!selectedOrganization) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = {
        organization_id: selectedOrganization,
        sort_by_field: 'total_revenue',
        sort_order: 'desc',
      };
      const res = await api.get('/api/v2/offer_reports/', { params });
      setRows(Array.isArray(res.data) ? res.data : []);
      setSortByField('total_revenue');
      setSortOrder('desc');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : err?.message || 'Failed to load offer reports.'
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    if (!selectedOrganization) return;
    loadRows();
  }, [selectedOrganization, loadRows]);

  useEffect(() => {
    setExpandedAttributeKey(null);
    setFeatureValues({});
    setFeatureValuesError('');
    setFeatureValuesLoading(false);

    if (!detailsOffer || !selectedOrganization) {
      setTopAttributes([]);
      setBottomAttributes([]);
      setTopAttributesError('');
      setBottomAttributesError('');
      setTopAttributesLoading(false);
      setBottomAttributesLoading(false);
      setTopPairs({ positive: [], negative: [], warning: null });
      setTopPairsError('');
      setTopPairsLoading(false);
      return undefined;
    }

    const { features: featuresPath, topPairs: topPairsPath } =
      detailsApiPaths(detailsOffer);

    let cancelled = false;
    const loadAttributes = async (sortOrder, setRows, setLoading, setError, errorFallback) => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(featuresPath, {
          params: {
            organization_id: selectedOrganization,
            sort_order: sortOrder,
            limit: ATTRIBUTES_LIMIT,
          },
        });
        if (!cancelled) {
          setRows(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setError(
            typeof detail === 'string' ? detail : err?.message || errorFallback
          );
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    const loadTopPairs = async () => {
      setTopPairsLoading(true);
      setTopPairsError('');
      try {
        const res = await api.get(topPairsPath, {
          params: { organization_id: selectedOrganization },
        });
        if (!cancelled) {
          setTopPairs({
            positive: Array.isArray(res.data?.positive) ? res.data.positive : [],
            negative: Array.isArray(res.data?.negative) ? res.data.negative : [],
            warning: res.data?.warning ?? null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setTopPairsError(
            typeof detail === 'string'
              ? detail
              : err?.message || 'Failed to load top pairs.'
          );
          setTopPairs({ positive: [], negative: [], warning: null });
        }
      } finally {
        if (!cancelled) {
          setTopPairsLoading(false);
        }
      }
    };
    loadAttributes(
      'asc',
      setTopAttributes,
      setTopAttributesLoading,
      setTopAttributesError,
      'Failed to load top attributes.'
    );
    loadAttributes(
      'desc',
      setBottomAttributes,
      setBottomAttributesLoading,
      setBottomAttributesError,
      'Failed to load bottom attributes.'
    );
    if (topPairsPath) {
      loadTopPairs();
    } else {
      setTopPairs({ positive: [], negative: [], warning: null });
      setTopPairsError('');
      setTopPairsLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [detailsOffer, selectedOrganization]);

  const expandedAttributeFeature = expandedAttributeKey
    ? expandedAttributeKey.slice(expandedAttributeKey.indexOf(':') + 1)
    : null;

  useEffect(() => {
    if (!expandedAttributeFeature || !detailsOffer || !selectedOrganization) {
      return undefined;
    }
    if (featureValues[expandedAttributeFeature]) return undefined;

    const { featureValues: valuesPath } = detailsApiPaths(detailsOffer);

    let cancelled = false;
    const loadValues = async () => {
      setFeatureValuesLoading(true);
      setFeatureValuesError('');
      try {
        const res = await api.get(valuesPath, {
          params: {
            organization_id: selectedOrganization,
            feature: expandedAttributeFeature,
          },
        });
        if (!cancelled) {
          setFeatureValues((prev) => ({
            ...prev,
            [expandedAttributeFeature]: Array.isArray(res.data) ? res.data : [],
          }));
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setFeatureValuesError(
            typeof detail === 'string'
              ? detail
              : err?.message || 'Failed to load feature values.'
          );
        }
      } finally {
        if (!cancelled) {
          setFeatureValuesLoading(false);
        }
      }
    };
    loadValues();
    return () => {
      cancelled = true;
    };
  }, [
    expandedAttributeFeature,
    detailsOffer,
    selectedOrganization,
    featureValues,
  ]);

  const toggleAttribute = (section, feature) => {
    const key = `${section}:${feature}`;
    setFeatureValuesError('');
    setExpandedAttributeKey((prev) => (prev === key ? null : key));
  };

  const closeDetails = () => {
    setDetailsOffer(null);
    setTopAttributes([]);
    setBottomAttributes([]);
    setTopAttributesError('');
    setBottomAttributesError('');
    setTopAttributesLoading(false);
    setBottomAttributesLoading(false);
    setTopPairs({ positive: [], negative: [], warning: null });
    setTopPairsError('');
    setTopPairsLoading(false);
    setExpandedAttributeKey(null);
    setFeatureValues({});
    setFeatureValuesError('');
    setFeatureValuesLoading(false);
    setGeneratingSegment(false);
  };

  const handleGenerateSegment = async () => {
    const offer = detailsOffer;
    if (!offer || generatingSegment) return;
    const mode = getDetailsMode(offer);
    setGeneratingSegment(true);
    try {
      let result;
      if (mode === 'metrics' && Array.isArray(topPairs.positive) && topPairs.positive.length) {
        result = await buildCriteriaFromAttributeRows(topPairs.positive);
      } else if (mode === 'vertical' && offer.category) {
        result = await buildCriteriaFromVertical(offer.category);
      } else if (mode === 'general') {
        result = await buildCriteriaFromVertical(GENERAL_TEMPLATE_VALUE);
      } else if (topAttributes.length) {
        result = await buildCriteriaFromAttributeRows(topAttributes);
      } else {
        toast.error('No attributes available to generate a segment.');
        return;
      }
      if (result.applied === 0) {
        toast.error(
          result.total === 0
            ? 'No attributes available to generate a segment.'
            : 'Could not match any attributes to catalog columns.'
        );
        return;
      }
      if (result.skipped.length) {
        toast.success(
          `Applied ${result.applied} of ${result.total} attributes. Skipped: ${result.skipped.join(', ')}`
        );
      }
      const draftName = sanitizeSegmentName(
        offer.offer_name || `Offer ${offer.offer_id}`
      );
      const templateVertical =
        mode === 'vertical' && offer.category
          ? offer.category
          : mode === 'general'
            ? GENERAL_TEMPLATE_VALUE
            : '';
      navigate('/segments/new', {
        state: {
          offerReportDraft: {
            name: draftName,
            criteria: result.criteria,
            offer: {
              offer_id: offer.offer_id,
              offer_name: offer.offer_name || '',
            },
            templateVertical,
          },
        },
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : err?.message || 'Failed to generate segment'
      );
    } finally {
      setGeneratingSegment(false);
    }
  };

  /** Exports the report exactly as shown, so collapsed attributes stay collapsed. */
  const handleExportPng = async () => {
    const node = reportRef.current;
    if (!node || exportingPng) return;
    const offer = detailsOffer;
    setExportingPng(true);
    try {
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        backgroundColor: REPORT_COLORS.paper,
        cacheBust: true,
        filter: (element) => {
          if (!(element instanceof Element)) return true;
          return !element.closest('[data-export-exclude]');
        },
      });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = exportFileName(offer);
      link.click();
    } catch {
      toast.error('Unable to export the offer report image');
    } finally {
      setExportingPng(false);
    }
  };

  const featureLabelByName = useMemo(() => {
    const map = {};
    for (const feature of [...topAttributes, ...bottomAttributes]) {
      if (feature?.feature) {
        map[feature.feature] = formatFeatureLabel(feature);
      }
    }
    return map;
  }, [topAttributes, bottomAttributes]);

  const includeRows = useMemo(
    () => toPairRows(topPairs.positive, featureLabelByName),
    [topPairs.positive, featureLabelByName]
  );
  const excludeRows = useMemo(
    () => toPairRows(topPairs.negative, featureLabelByName),
    [topPairs.negative, featureLabelByName]
  );
  const warningValue = useMemo(
    () => formatWarningClause(topPairs.warning, featureLabelByName),
    [topPairs.warning, featureLabelByName]
  );

  const sortedOrganizations = useMemo(
    () => [...organizations].sort((a, b) => a.name.localeCompare(b.name)),
    [organizations]
  );

  const handleSearchResults = useCallback(({ query, hits, loading: resultsLoading, error }) => {
    const trimmed = (query || '').trim();
    if (resultsLoading) {
      setSearching(Boolean(trimmed));
      return;
    }
    setSearching(false);
    setListSearchError(error || '');
    if (trimmed !== searchQueryRef.current) {
      searchQueryRef.current = trimmed;
      resetPage();
    }
    if (!trimmed) {
      setMatchIds(null);
      return;
    }
    setMatchIds((Array.isArray(hits) ? hits : []).map((hit) => String(hit.offer_id)));
  }, [resetPage]);

  const sortedRows = useMemo(() => {
    const typeaheadOrder =
      matchIds != null && sortByField === 'total_revenue' && sortOrder === 'desc';
    let source = rows;
    if (matchIds != null) {
      const byId = new Map(rows.map((row) => [String(row.offer_id), row]));
      source = matchIds.map((id) => byId.get(id)).filter(Boolean);
    }
    if (typeaheadOrder) return source;
    const copy = [...source];
    copy.sort((a, b) => compareOfferRows(a, b, sortByField, sortOrder));
    return copy;
  }, [rows, matchIds, sortByField, sortOrder]);

  const pagedRows = useMemo(
    () => sortedRows.slice(listOffset, listOffset + LIST_PAGE_SIZE),
    [sortedRows, listOffset]
  );

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / LIST_PAGE_SIZE) || 1);

  useEffect(() => {
    if (loading || sortedRows.length === 0) return;
    if (currentPage > totalPages) {
      goToPage(totalPages);
    }
  }, [loading, sortedRows.length, currentPage, totalPages, goToPage]);

  const toggleSort = (field) => {
    resetPage();
    if (sortByField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortByField(field);
      setSortOrder(
        field === 'offer_name' || field === 'advertiser_name' || field === 'category'
          ? 'asc'
          : 'desc'
      );
    }
  };

  const renderSortableHeader = (label, field, className, align) => {
    const active = sortByField === field;
    const alignRight = align === 'right';
    return (
      <th
        className={`font-medium text-gray-700 ${className} ${
          alignRight ? 'text-right' : 'text-left'
        }`}
      >
        <button
          type="button"
          onClick={() => toggleSort(field)}
          className={`inline-flex items-center gap-1 hover:text-gray-900 ${
            alignRight ? 'justify-end' : ''
          }`}
        >
          {label}
          {active ? (
            sortOrder === 'asc' ? (
              <ChevronUp size={14} />
            ) : (
              <ChevronDown size={14} />
            )
          ) : null}
        </button>
      </th>
    );
  };

  const CONVERTERS_BAR = {
    key: 'pct_of_converters',
    color: REPORT_COLORS.blue,
    format: formatPercent,
  };
  const CLICKERS_BAR = {
    key: 'pct_of_clickers',
    color: REPORT_COLORS.clickers,
    format: formatPercent,
  };

  /**
   * Which value bar and conversion rate a row can show. Each report type
   * returns a different subset of these fields, and `values` is undefined
   * until an attribute is expanded, so an unloaded attribute assumes the
   * widest set for its type.
   */
  const valueMetricsFor = (values, isVertical, isLite) => {
    const hasRows = Boolean(values?.length);
    const present = (field) => !hasRows || !allFieldMissing(values, field);
    const valueBar = isLite ? CLICKERS_BAR : CONVERTERS_BAR;
    const showValueBar = isLite ? present('pct_of_clickers') : true;
    return {
      showRate: !isVertical && (!isLite || present('conv_rate')),
      valueBar: showValueBar ? valueBar : null,
      baselinePct: hasRows ? 100 / values.length : null,
    };
  };

  const attributeSummary = (feature, isVertical, isLite) => {
    const topValueMissing = isMissingValue(feature.top_conv_value);
    if (topValueMissing) return null;
    const valueText = formatValueLabel({
      value: feature.top_conv_value,
      value_label: feature.top_conv_value_label,
    });
    if (!isMissingValue(feature.top_conv_rate)) {
      return isVertical
        ? `${valueText} is ${formatPercent(feature.top_conv_rate)} of converters`
        : `${valueText} converts ${formatPercent(feature.top_conv_rate)}`;
    }
    if (isLite && !isMissingValue(feature.top_pct_of_clickers)) {
      return `${valueText} is ${formatPercent(feature.top_pct_of_clickers)} of clickers`;
    }
    if (isLite && !isMissingValue(feature.top_pct_of_universe)) {
      return `${valueText} is ${formatUniverseShare(
        feature.top_pct_of_universe
      )} of the list`;
    }
    return null;
  };

  const renderAttributesSection = ({
    section,
    title,
    tooltip,
    attributes,
    loading,
    error,
    emptyMessage,
    loadingMessage,
    isVertical,
    isLite,
  }) => (
    <ReportSection
      title={<SectionTitleWithTooltip tooltip={tooltip}>{title}</SectionTitleWithTooltip>}
    >
      {loading ? (
        <p className="text-sm text-[#8A8F98]">{loadingMessage}</p>
      ) : error ? (
        <p className="text-sm text-[#FF87AA]">{error}</p>
      ) : attributes.length === 0 ? (
        <p className="text-sm text-[#8A8F98]">{emptyMessage}</p>
      ) : (
        <div className="grid grid-cols-1 items-start gap-3.5 sm:grid-cols-2">
          {attributes.map((feature, index) => {
            const attributeKey = `${section}:${feature.feature}`;
            const isExpanded = expandedAttributeKey === attributeKey;
            const values = featureValues[feature.feature];
            const summary = attributeSummary(feature, isVertical, isLite);
            const { showRate, valueBar, baselinePct } = valueMetricsFor(
              values,
              isVertical,
              isLite
            );
            return (
              <div
                key={attributeKey}
                className="rounded-[18px] border border-[#EEF2F7] bg-[#f4f7fb] shadow-[0_1px_2px_rgba(22,27,34,0.05)]"
              >
                <button
                  type="button"
                  onClick={() => toggleAttribute(section, feature.feature)}
                  aria-expanded={isExpanded}
                  className="w-full rounded-[18px] px-[18px] py-4 text-left hover:bg-[#EEF2F7]"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex min-w-0 items-baseline gap-1.5">
                      <span className="flex-none text-[#8A8F98]">
                        {isExpanded ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </span>
                      <span className="flex-none text-[13px] tabular-nums text-[#8A8F98]">
                        {`${index + 1}.`}
                      </span>
                      <span className="min-w-0 break-words text-base font-semibold text-[#161B22]">
                        {formatFeatureLabel(feature)}
                      </span>
                    </div>
                    {summary ? (
                      <span className="min-w-0 shrink break-words text-right text-[12.5px] text-[#8A8F98]">
                        {summary}
                      </span>
                    ) : null}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-[18px] pb-1">
                    {featureValuesLoading && !values ? (
                      <p className="pb-2 text-sm text-[#8A8F98]">Loading values…</p>
                    ) : featureValuesError && !values ? (
                      <p className="pb-2 text-sm text-[#FF87AA]">{featureValuesError}</p>
                    ) : !values || values.length === 0 ? (
                      <p className="pb-2 text-sm text-[#8A8F98]">
                        No values for this attribute.
                      </p>
                    ) : (
                      <>
                        <p className="mb-2.5 text-xs text-[#8A8F98]">
                          {ATTRIBUTE_BAR_CAPTION}
                        </p>
                        <div>
                          {values.map((row) => (
                            <div
                              key={row.value}
                              className="border-t border-[#EEF2F7] py-[9px] first:border-t-0"
                            >
                              <div className="mb-1.5 flex items-baseline justify-between gap-3">
                                <span className="min-w-0 break-words text-sm font-medium text-[#161B22]">
                                  {formatValueLabel(row)}
                                </span>
                                {showRate ? (
                                  <span className="flex-none text-sm text-[#8A8F98]">
                                    <span className="font-semibold tabular-nums text-[#161B22]">
                                      {formatPercent(row.conv_rate)}
                                    </span>
                                    {' conv.'}
                                  </span>
                                ) : null}
                              </div>
                              <div className="flex flex-col gap-1">
                                {valueBar ? (
                                  <ValueBar
                                    label="Value"
                                    color={valueBar.color}
                                    value={row[valueBar.key]}
                                    format={valueBar.format}
                                  />
                                ) : null}
                                {baselinePct != null ? (
                                  <ValueBar
                                    label="Baseline"
                                    color={REPORT_COLORS.baseline}
                                    value={baselinePct}
                                    format={formatPercent}
                                  />
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </ReportSection>
  );

  const detailsMode = getDetailsMode(detailsOffer);
  const metricView = detailsMode === 'values' ? 'clicks' : 'conversions';
  const canGenerateSegment =
    (detailsMode === 'metrics' && includeRows.length > 0) ||
    (detailsMode === 'vertical' && Boolean(detailsOffer?.category)) ||
    detailsMode === 'general' ||
    topAttributes.length > 0;

  return (
    <div className="space-y-6">
      <PageHeaderActions>
        <FunctionalSelect
          label="Organization"
          className={HEADER_SELECT_CLASS}
          value={organizationsService.getSelectedOptionValue(
            sortedOrganizations,
            selectedOrganization,
            'id'
          )}
          onValueChange={(value) => {
            const parsed = organizationsService.parseOrganizationOptionValue(value);
            resetPage();
            searchQueryRef.current = '';
            setMatchIds(null);
            setSearching(false);
            setListSearchError('');
            if (parsed) {
              setSelectedOrganization(parsed.id);
              organizationsService.setSelectedOrganization(
                parsed.id,
                'id',
                sortedOrganizations
              );
            } else {
              setSelectedOrganization(value);
              organizationsService.setSelectedOrganization(
                value,
                'id',
                organizations
              );
            }
          }}
          placeholder="Select organization"
        >
          {sortedOrganizations.map((org) => (
            <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
              {org.name}
            </SelectItem>
          ))}
        </FunctionalSelect>
      </PageHeaderActions>
      <Card className="overflow-visible">
        <CardContent className="pt-6 space-y-4 overflow-visible">
          {selectedOrganization && (
            <OfferSearchTypeahead
              suggestions={false}
              resetKey={selectedOrganization}
              organizationId={selectedOrganization}
              onResults={handleSearchResults}
            />
          )}

          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          {listSearchError ? <p className="text-sm text-red-600">{listSearchError}</p> : null}

          {loading || (searching && matchIds == null) ? (
            <p className="text-sm text-gray-500">
              {loading ? 'Loading offer reports…' : 'Searching…'}
            </p>
          ) : sortedRows.length === 0 ? (
            <p className="text-sm text-gray-500">
              {matchIds != null
                ? 'No offers match this search.'
                : 'No offer reports for this organization.'}
            </p>
          ) : (
            <>
              <div className="border border-gray-200 rounded-md overflow-x-auto">
                <table className="min-w-[56rem] w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {SORTABLE_COLUMNS.map((col) =>
                        renderSortableHeader(
                          col.label,
                          col.key,
                          col.key === 'offer_id'
                            ? 'pl-3 pr-3 py-2 border-b whitespace-nowrap'
                            : 'px-3 py-2 border-b whitespace-nowrap',
                          col.align
                        )
                      )}
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[5rem]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row) => (
                      <tr
                        key={row.offer_id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="pl-3 pr-3 py-2 text-gray-800 whitespace-nowrap">
                          {row.offer_id}
                        </td>
                        <td
                          className="px-3 py-2 font-medium max-w-[14rem] whitespace-normal break-words text-gray-800"
                          title={row.offer_name || undefined}
                        >
                          {row.offer_name || '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-800 max-w-[12rem] whitespace-normal break-words">
                          {row.advertiser_name || '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                          {row.category || '—'}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-800 whitespace-nowrap tabular-nums">
                          {formatCount(row.total_click)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-800 whitespace-nowrap tabular-nums">
                          {formatRevenue(row.total_revenue)}
                        </td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                          {formatDate(row.created)}
                        </td>
                        <td className="px-3 py-2">
                          {/* Every row here already has an offer_report join (list_offer_reports),
                              so it always has some flavor — full/lite/vertical, or general as the
                              fallback when none of those flags are set. */}
                          <Link
                            to={`/offer-report/${row.offer_id}`}
                            state={listPageState(currentPage)}
                            className="p-1 rounded shrink-0 inline-flex text-[#0062FB] hover:bg-blue-50"
                            aria-label={`Open audience report for ${row.offer_name || row.offer_id}`}
                            title="Audience report: converters vs. baseline"
                          >
                            <Users className="h-4 w-4" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 text-sm text-gray-600">
                <span>
                  {sortedRows.length.toLocaleString()} offer
                  {sortedRows.length === 1 ? '' : 's'}
                  {` · page ${currentPage} of ${totalPages}`}
                </span>
                <ListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  disabled={loading}
                  onPageChange={goToPage}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={detailsOffer != null}
        onClose={closeDetails}
        className="w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-[#f4f7fb]"
      >
        <div ref={reportRef} className="bg-[#f4f7fb] px-10 pb-9 pt-8 text-[#161B22]">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-6 pr-12">
            <div className="min-w-0">
              <h2 className="mb-1.5 max-w-[720px] text-[34px] font-bold leading-[1.15] tracking-[-0.02em] text-[#161B22]">
                {detailsOffer?.offer_name || '—'}
              </h2>
              <p className="text-[15px] text-[#8A8F98]">
                <strong className="font-medium text-[#161B22]">
                  {detailsOffer?.advertiser_name || '—'}
                </strong>
                <span className="mx-2.5 text-[#E2E8F0]">·</span>
                {detailsOffer?.category || '—'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <HeaderPill label="Offer:" value={detailsOffer?.offer_id ?? '—'} />
              <HeaderPill
                label="Created:"
                value={formatReportDate(detailsOffer?.created)}
              />
              <HeaderPill label="Type:" value={detailsTypeLabel(detailsOffer)} />
              {canExport && (
                <button
                  type="button"
                  data-export-exclude=""
                  onClick={handleExportPng}
                  disabled={exportingPng}
                  aria-label={exportingPng ? 'Exporting PNG' : 'Export PNG'}
                  title={exportingPng ? 'Exporting…' : 'Export PNG'}
                  className="inline-flex items-center justify-center rounded-lg bg-[#0062FB] p-3 text-white hover:bg-[#0052E0] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-4 w-4 text-white" />
                </button>
              )}
            </div>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <KpiCell
              label="Total Clicks"
              iconTone="blue"
              icon={<MousePointerClick className="h-[17px] w-[17px]" />}
              value={formatCount(detailsOffer?.total_click)}
            />
            <KpiCell
              label="Total Revenue"
              iconTone="green"
              icon={<DollarSign className="h-[17px] w-[17px]" />}
              value={formatRevenue(detailsOffer?.total_revenue)}
            />
          </div>

          <ReportSection
            title="Recommendations"
            actions={
              canCreateSegments ? (
                <button
                  type="button"
                  data-export-exclude=""
                  onClick={handleGenerateSegment}
                  disabled={generatingSegment || !canGenerateSegment}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#0062FB] px-[18px] py-2.5 text-[13.5px] font-semibold text-white hover:bg-[#0052E0] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {generatingSegment ? 'Generating…' : 'Generate segment'}
                </button>
              ) : null
            }
          >
            {detailsMode === 'metrics' ? (
              topPairsLoading ? (
                <p className="text-sm text-[#8A8F98]">
                  Loading include / exclude summary…
                </p>
              ) : topPairsError ? (
                <p className="text-sm text-[#FF87AA]">{topPairsError}</p>
              ) : includeRows.length || excludeRows.length || warningValue ? (
                <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-3">
                  {includeRows.length ? (
                    <RecommendationCard tone="include" title="Attributes to include">
                      <PairList rows={includeRows} />
                    </RecommendationCard>
                  ) : null}
                  {excludeRows.length ? (
                    <RecommendationCard tone="exclude" title="Attributes to exclude">
                      <PairList rows={excludeRows} />
                    </RecommendationCard>
                  ) : null}
                  {warningValue ? (
                    <RecommendationCard tone="warning" title="Warning">
                      <p className="text-sm text-[#2B323C]">{warningValue}</p>
                    </RecommendationCard>
                  ) : null}
                  {detailsOffer?.note?.trim() ? (
                    <NotesCard>
                      <p className="m-0 whitespace-pre-wrap">{detailsOffer.note}</p>
                    </NotesCard>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-[#8A8F98]">
                  No include / exclude pairs for this offer.
                </p>
              )
            ) : (
              <NotesCard>
                {detailsOffer?.note?.trim() ? (
                  <p className="m-0 whitespace-pre-wrap">{detailsOffer.note}</p>
                ) : (
                  <p className="m-0 text-[#8A8F98]">No notes for this offer.</p>
                )}
              </NotesCard>
            )}
          </ReportSection>

          {renderAttributesSection({
            section: 'top',
            title: 'Top Attributes',
            tooltip: TOP_ATTRIBUTES_TOOLTIP,
            attributes: topAttributes,
            loading: topAttributesLoading,
            error: topAttributesError,
            emptyMessage: 'No top attributes for this offer.',
            loadingMessage: 'Loading top attributes…',
            isVertical: isCategoryDetailsMode(detailsMode),
            isLite: detailsMode === 'values',
          })}

          {renderAttributesSection({
            section: 'bottom',
            title: 'Bottom Attributes',
            tooltip: BOTTOM_ATTRIBUTES_TOOLTIP,
            attributes: bottomAttributes,
            loading: bottomAttributesLoading,
            error: bottomAttributesError,
            emptyMessage: 'No bottom attributes for this offer.',
            loadingMessage: 'Loading bottom attributes…',
            isVertical: isCategoryDetailsMode(detailsMode),
            isLite: detailsMode === 'values',
          })}

          <div className="mb-4">
            <ReportLegend metricView={metricView} />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 px-1 pt-2.5">
            <p className="text-xs text-[#8A8F98]">
              Offer performance report · generated from campaign attribution data
            </p>
            <div data-export-exclude="">
              <Button type="button" variant="outline" onClick={closeDetails}>
                OK
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <ErrorDialog
        isOpen={Boolean(accessError)}
        onClose={() => setAccessError('')}
        message={accessError}
      />
    </div>
  );
}

export default OfferReport;
